import { Catalog, SingleSourceOfTruth } from '@philips-internal/oswald-ui-core';
import { ComponentAPI, configureAPI } from '@philips-internal/oswald-ui-core/lib/component-api.js';
import { ExperimentalFlagsService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { noop } from '@philips-internal/oswald-ui-utils';
import { PropsProcessor } from '../bridges/props-processor.js';
import { resolveProps } from '../utils/index.js';
import { EngineEventDelegator } from './engine-event-delegator.js';
const engineProperties = [
    'alias',
];
/**
 * Component node presentation.
 */
class ComponentNode {
    constructor(args) {
        this.revokeAPIProxy = noop;
        this.children = new Map();
        this.instanceUUID = args.instanceUUID;
        this.nodeMetadata = args.nodeMetadata;
        this.owner = args.owner;
        this.type = this.nodeMetadata.type;
        this.uuid = args.uuid;
        const componentData = Catalog.get(this.type);
        const defaultProps = componentData.getDefaultProps();
        const propTypes = componentData.getPropTypes();
        // TODO: it might have a better way to specify engine props (Ex.: alias)
        const propsDefinition = [...Object.keys(propTypes), ...engineProperties];
        this.ssot = new SingleSourceOfTruth(this.getHashCode(), defaultProps, propsDefinition);
        this.ssot.setMetaProps(resolveProps(this.nodeMetadata.props));
        this.eventDelegator = new EngineEventDelegator(this);
        const API = componentData.getAPI() ?? ComponentAPI;
        if (ExperimentalFlagsService.getProxyWrappedAPIs()) {
            const { proxy, revoke } = Proxy.revocable(new API(), {});
            this.api = proxy;
            this.revokeAPIProxy = revoke;
        }
        else {
            this.api = new API();
        }
        configureAPI({
            eventDelegator: this.eventDelegator,
            getChildrenAPIs: () => this.getChildrenAPIs(),
            instance: this.api,
            ssot: this.ssot,
        });
        this.propsProcessor = new PropsProcessor(this, this.owner);
    }
    /**
     * Destroy all resources created by the component instance.
     */
    destroy() {
        this.revokeAPIProxy();
    }
    /**
     * Create a unique key using the child node own UUID and Instance UUID.
     *
     * @param child - The child used to create the key.
     */
    createChildKey(child) {
        return `${child.getUUID()}_${child.getInstanceUUID()}`;
    }
    /**
     * Adds a child node to the component.
     *
     * @param child - The child being added.
     */
    addChild(child) {
        const key = this.createChildKey(child);
        this.children.set(key, child);
    }
    /**
     * Removes a given child node from the component.
     *
     * @param child - The child being removed.
     */
    removeChild(child) {
        const key = this.createChildKey(child);
        this.children.delete(key);
    }
    /**
     * Returns a map with all of the component children APIs.
     */
    // @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
    getChildrenAPIs() {
        // @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
        const apis = new Map();
        for (const [key, value] of this.children) {
            apis.set(key, value.getAPI());
        }
        return apis;
    }
    /**
     * Retrieves the node API instance.
     */
    // @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
    getAPI() {
        return this.api;
    }
    /**
     * Retrieves the node EngineEventDelegator instance.
     */
    getEventDelegator() {
        return this.eventDelegator;
    }
    /**
     * Retrieves the instance hash-code.
     */
    getHashCode() {
        const rootHashCode = this.owner.getHashCode();
        return `${rootHashCode}_${this.uuid}`;
    }
    /**
     * Retrieves the node metadata.
     */
    getNodeMetadata() {
        return this.nodeMetadata;
    }
    /**
     * Retrieves the node owner.
     */
    getOwner() {
        return this.owner;
    }
    /**
     * Retrieves the node PropsProcessor
     */
    getPropsProcessor() {
        return this.propsProcessor;
    }
    /**
     * Retrieves the ComponentType that must be rendered.
     */
    getRenderable() {
        const data = Catalog.get(this.type);
        return data.getComponent();
    }
    /**
     * Retrieves the node registered Prop Types.
     */
    getPropTypes() {
        return Catalog.get(this.getType()).getPropTypes();
    }
    /**
     * Retrieves the node default props and their values.
     */
    getDefaultProps() {
        return Catalog.get(this.getType()).getDefaultProps();
    }
    /**
     * Retrieves the node SingleSourceOfTruth instance.
     */
    // @ts-ignore: Please check the comment at the top of the file for the explanation on why we need this ignore.
    getSSOT() {
        return this.ssot;
    }
    /**
     * Retrieves the node type;
     */
    getType() {
        return this.type;
    }
    /**
     * Retrieves the node instance uuid.
     */
    getInstanceUUID() {
        return this.instanceUUID;
    }
    /**
     * Retrieves the node uuid.
     */
    getUUID() {
        return this.uuid;
    }
}
export { ComponentNode, };
//# sourceMappingURL=component-node.js.map